import React, { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { noop } from 'lodash'
import PropTypes from 'prop-types'

import { idPropType, nodePropType } from '../../prop-types'

import MetadataTree from './metadata-tree'

const ReduxMetadataTree = ({
  disableLink,
  mainTreeExpandedKeys,
  supportingTreeExpandedKeys,
  expandMainTree,
  expandSupportingTree,
  metadataType,
  secondaryMetadataType,
  isDirty,
  showSearch,
  treeData,
  editable,
  addNode,
  deleteNode,
  renameNode,
  setDataForRenaming,
  setIsCurationEnabled,
  dataForRenaming,
  allowReplaceMany,
  moveNodes,
  replaceNode,
  revertReplaceNode,
  computeNodeHighlight,
  nodeTitleContentRender,
  showRenameInput,
  shouldPickSelectedSupportingItem,
  isMoveActive,
  setIsMoveActive,
  isReplaceActive,
  setIsReplaceActive,
  getLink,
}) => {
  const dispatch = useDispatch()

  const handleMainTreeOnExpand = useCallback(
    expandedKeys => {
      dispatch(expandMainTree(expandedKeys))
    },
    [dispatch, expandMainTree],
  )

  const handleSupportingTreeOnExpand = useCallback(
    expandedKeys => {
      dispatch(expandSupportingTree(expandedKeys))
    },
    [dispatch, expandSupportingTree],
  )

  return (
    <MetadataTree
      disableLink={disableLink}
      mainTreeExpandedKeys={mainTreeExpandedKeys}
      supportingTreeExpandedKeys={supportingTreeExpandedKeys}
      onMainTreeExpand={handleMainTreeOnExpand}
      onSupportingTreeExpand={handleSupportingTreeOnExpand}
      metadataType={metadataType}
      secondaryMetadataType={secondaryMetadataType}
      isDirty={isDirty}
      showSearch={showSearch}
      allowReplaceMany={allowReplaceMany}
      treeData={treeData}
      editable={editable}
      addNode={addNode}
      deleteNode={deleteNode}
      renameNode={renameNode}
      moveNodes={moveNodes}
      replaceNode={replaceNode}
      revertReplaceNode={revertReplaceNode}
      setDataForRenaming={setDataForRenaming}
      setIsCurationEnabled={setIsCurationEnabled}
      dataForRenaming={dataForRenaming}
      computeNodeHighlight={computeNodeHighlight}
      nodeTitleContentRender={nodeTitleContentRender}
      showRenameInput={showRenameInput}
      shouldPickSelectedSupportingItem={shouldPickSelectedSupportingItem}
      isMoveActive={isMoveActive}
      setIsMoveActive={setIsMoveActive}
      isReplaceActive={isReplaceActive}
      setIsReplaceActive={setIsReplaceActive}
      getLink={getLink}
    />
  )
}

ReduxMetadataTree.propTypes = {
  disableLink: PropTypes.bool,
  mainTreeExpandedKeys: PropTypes.arrayOf(PropTypes.string),
  supportingTreeExpandedKeys: PropTypes.arrayOf(PropTypes.string),
  expandMainTree: PropTypes.func,
  expandSupportingTree: PropTypes.func,
  metadataType: PropTypes.object.isRequired,
  secondaryMetadataType: PropTypes.object,
  isDirty: PropTypes.bool,
  treeData: PropTypes.arrayOf(nodePropType),
  showSearch: PropTypes.bool,
  editable: PropTypes.bool,
  addNode: PropTypes.func,
  deleteNode: PropTypes.func,
  renameNode: PropTypes.func,
  setDataForRenaming: PropTypes.func,
  setIsCurationEnabled: PropTypes.func,
  dataForRenaming: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
    PropTypes.shape({ id: idPropType, type: PropTypes.string }),
  ]),
  allowReplaceMany: PropTypes.bool,
  moveNodes: PropTypes.func,
  replaceNode: PropTypes.func,
  revertReplaceNode: PropTypes.func,
  computeNodeHighlight: PropTypes.func,
  nodeTitleContentRender: PropTypes.func,
  showRenameInput: PropTypes.func,
  shouldPickSelectedSupportingItem: PropTypes.func,
  isMoveActive: PropTypes.bool,
  setIsMoveActive: PropTypes.func,
  isReplaceActive: PropTypes.bool,
  setIsReplaceActive: PropTypes.func,
  getLink: PropTypes.func,
}

ReduxMetadataTree.defaultProps = {
  secondaryMetadataType: undefined,
  disableLink: false,
  mainTreeExpandedKeys: [],
  supportingTreeExpandedKeys: [],
  expandMainTree: noop,
  expandSupportingTree: noop,
  isDirty: false,
  treeData: [],
  showSearch: false,
  editable: false,
  addNode: undefined,
  deleteNode: undefined,
  renameNode: undefined,
  setDataForRenaming: undefined,
  setIsCurationEnabled: undefined,
  dataForRenaming: undefined,
  allowReplaceMany: false,
  moveNodes: undefined,
  replaceNode: undefined,
  revertReplaceNode: undefined,
  computeNodeHighlight: undefined,
  nodeTitleContentRender: undefined,
  showRenameInput: undefined,
  shouldPickSelectedSupportingItem: undefined,
  isMoveActive: false,
  setIsMoveActive: noop,
  isReplaceActive: false,
  setIsReplaceActive: noop,
  getLink: undefined,
}

export default ReduxMetadataTree
